import styled, {css} from "styled-components";
import {createCard, fontFamilies, radii} from "theme/tokens";
import {POSTER_REGION_INSET} from "../takeover/SponsorPoster.styles";
import {
	WALLPAPER_PREVIEW_CARD_TESTID,
	WALLPAPER_PREVIEW_STAGE_TESTID,
} from "../takeover/takeoverTestIds";

/**
 * Externalized styles for {@link ./WallpaperPreview} (kitecorp/transferify-ui#1868).
 *
 * The stage stands in for the recipient's whole page, at the size a booking form can spare.
 */

/**
 * The phone stage's real geometry, and how far it is shrunk to fit a booking form's column.
 *
 * 390x844 is the viewport every narrow case in this repo is measured at. The scale is the one number
 * here that is taste rather than fact: it makes the phone about as tall as the desktop stage beside it
 * at the widths the booking form uses, which is what lets the two read as one comparison.
 */
const PHONE_STAGE = {width: 390, height: 844, scale: 0.38} as const;

/**
 * The recipient window the desktop stage stands for: the 1280x720 laptop the owner reviews on, which is
 * also why the stage is 16:9. The ghost card takes the share of it the real card does, from the same
 * token the recipient page lays that card out with, so a new card width moves the preview with it
 * (kitecorp/transferify-ui#2928). A fixed share stood here, and drifted when the real card narrowed.
 */
const DESKTOP_STAGE_WINDOW_PX = 1280;
const DESKTOP_CARD_SHARE = `${(createCard.sideBySideWidthPx / DESKTOP_STAGE_WINDOW_PX) * 100}%`;

/**
 * The bounded page this preview composes inside.
 *
 * It PUBLISHES the poster's region ({@link POSTER_REGION_INSET}), because the poster's own stylesheet
 * deliberately does not know where any surface's download card is — see its note. On a real recipient
 * page that inset is pixels off the window; here it is a fraction of the stage, so the composition
 * holds at whatever width the form's column happens to be.
 *
 * The DESKTOP stage alone reserves the card column. The phone stage composes over its whole box, which
 * is what a phone recipient gets, and it is a separate element so it simply inherits nothing from here.
 *
 * NO COOKIE-BAR RESERVE in either, and that is a fix rather than an omission
 * (kitecorp/transferify-ui#2403). This stage used to hand the poster `--tf-recipient-pad-b: 0px`, which
 * made the region's bottom `max(0px, --consent-bar-reserve - 0px)` — the FULL reserve the banner
 * publishes on the document root. A bounded stage has no bar under it, so while a visitor had not yet
 * answered the banner the preview lifted the advertiser's copy off the stage's own bottom edge and
 * showed them a composition no recipient would get.
 */
export const PreviewStage = styled.div.attrs<{"data-testid"?: string}>(
	{"data-testid": WALLPAPER_PREVIEW_STAGE_TESTID},
)<{$phone?: boolean}>`
	--tf-recipient-showcase-gap: 4%;
	position: relative;
	overflow: hidden;
	/*
	 * The two shapes one booking is shown in (kitecorp/transferify-ui#2143): the desktop page, elastic
	 * at 16:9, and a phone at 390x844 - the viewport the recipient page's own narrow cases are measured
	 * at. A phone keeps roughly a third of a 16:9 file's width, which is why the crop needs choosing at
	 * all, and a preview that only ever showed the wide shape could not have shown an advertiser that.
	 *
	 * THE PHONE IS LAID OUT AT ITS REAL SIZE AND THEN SCALED DOWN, rather than given a phone's ratio at
	 * thumbnail width, and that is the whole difference between a preview and a decoration. The poster
	 * block sizes its type against this box (SponsorPoster.styles.ts declares container-type on the
	 * poster region) and its clamps have a floor in pixels, so a 116px-wide stage typesets a headline
	 * one letter per line - measured, before this. At 390px the composition is the one a recipient's
	 * phone actually gets, and the transform is what makes it fit a booking form's column. Everything a
	 * transform scales is geometry; the crop, the placement and the contrast are decided before it.
	 * No backticks in this comment: it lives INSIDE a template literal and one would end it.
	 */
	${({$phone}) => ($phone
		? css`
			/*
			 * The recipient card at its REAL phone width: createCard.widthPx is 404 and the page's own
			 * 20px insets cap it, which is what a 390px window leaves. No backticks in this comment: it
			 * lives INSIDE a template literal and one would end it.
			 */
			--tf-recipient-card-w: 350px;
			width: ${PHONE_STAGE.width}px;
			height: ${PHONE_STAGE.height}px;
			transform: scale(${PHONE_STAGE.scale});
			transform-origin: top left;
		`
		: css`
			--tf-recipient-card-w: ${DESKTOP_CARD_SHARE};
			/* The space the ghost card leaves: the stage, minus that column and the gap beside it. */
			${POSTER_REGION_INSET}: 0 calc(var(--tf-recipient-card-w) + var(--tf-recipient-showcase-gap)) 0 0;
			aspect-ratio: 16 / 9;
		`)}
	border-radius: ${radii.panel};
	border: 1px solid ${({theme}) => theme.tokens.line};
	background: ${({theme}) => theme.tokens.field};
	/*
	 * Nothing in here is a control. The poster mounts a real anchor to the advertiser's own link, and a
	 * preview that navigated away mid-booking would lose the form. The inert attribute on the element is
	 * what takes it out of the tab order AND the pointer path; this rule covers the pointer only, so it
	 * is a second line of defence for the mouse rather than a fallback for a browser without inert -
	 * such a browser would still reach the link by keyboard. Every engine has shipped inert since 2023.
	 * No backticks in this comment: it lives INSIDE a template literal and one would end it.
	 */
	& a {
		pointer-events: none;
	}
`;

/**
 * The stand-in for the recipient's own download card, so the poster has something to compose beside.
 *
 * On the RIGHT, because that is where the real card is (kitecorp/transferify-ui#1922). This is not
 * decoration: `PosterRegion` positions itself against the same two custom properties this stage
 * declares, so a ghost on the wrong side would put the advertiser's copy over the card in the preview
 * and nowhere near it on the page the booking actually buys.
 */
export const CardGhost = styled.div.attrs<{"data-testid"?: string}>(
	{"data-testid": WALLPAPER_PREVIEW_CARD_TESTID},
)<{$phone?: boolean}>`
	position: absolute;
	/*
	 * Beside the art on the desktop stage, and STACKED under it on the phone one - which is what the
	 * recipient page itself does below its showcase breakpoint: the card takes the width and the art
	 * runs full bleed behind it (kitecorp/transferify-ui#2143). A phone ghost pinned to the right
	 * would describe a layout no recipient gets.
	 */
	${({$phone}) => ($phone
		? "left: 50%; transform: translateX(-50%); top: 58%; bottom: 5%;"
		: "right: 3%; top: 12%; bottom: 12%;")}
	width: var(--tf-recipient-card-w);
	border-radius: ${radii.panel};
	border: 1px dashed ${({theme}) => theme.tokens.fieldLine};
	background: ${({theme}) => theme.tokens.card};
	opacity: 0.72;
`;

/** The caption over the stage's corner, matching the template picker's own badge. */
export const PreviewBadge = styled.span`
	position: absolute;
	z-index: 3;
	left: 8px;
	bottom: 8px;
	padding: 3px 8px;
	border-radius: ${radii.chip};
	background: ${({theme}) => theme.tokens.card};
	border: 1px solid ${({theme}) => theme.tokens.cardBorder};
	color: ${({theme}) => theme.tokens.ink2};
	font-family: ${fontFamilies.mono};
	font-size: 9.5px;
	font-weight: 700;
	letter-spacing: 0.07em;
	text-transform: uppercase;
`;

/**
 * The two stages, side by side (kitecorp/transferify-ui#2143).
 *
 * The desktop stage takes the room and the phone stage keeps a fixed narrow column, because they are
 * pictures of two different SCREENS rather than two sizes of one: shrinking the desktop stage to match
 * the phone's height would make both of them miniatures and neither of them a page. It wraps below the
 * form's own narrow width, where a 200px-wide desktop stage says nothing anyway.
 */
export const PreviewStages = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: flex-start;
	gap: 16px;

	/* The wide stage takes what is left; 260px is the width below which its poster stops composing. */
	> :first-child {
		flex: 1 1 260px;
		min-width: 0;
	}
`;

/** The phone column: 390x844 at a size a booking form can spare, and never stretched by the row. */
export const PhoneColumn = styled.div`
	flex: 0 0 auto;
	/*
	 * The scaled footprint of the stage inside it. A transformed box still occupies its UNSCALED size
	 * in layout, so without these the column would reserve 390x844 and push the desktop stage off the
	 * row.
	 */
	width: ${Math.round(PHONE_STAGE.width * PHONE_STAGE.scale)}px;
	height: ${Math.round(PHONE_STAGE.height * PHONE_STAGE.scale)}px;
`;
